import React from "react";
import { Button } from "@ui/button";
import { Paintbrush2, Check } from "lucide-react";
import type { ColorScheme } from "@/core/data-tables/useDisplayOptions";

export interface DisplayOptionsProps {
  open: boolean;
  toggle: () => void;
  improveNames: boolean;
  setImproveNames: (value: boolean) => void;
  scheme: ColorScheme;
  setScheme: (scheme: ColorScheme) => void;
}

/**
 * Shared pop-over UI for:
 *  • toggling “Improve column names”
 *  • picking a colour scheme
 */
export function DisplayOptions({
  open,
  toggle,
  improveNames,
  setImproveNames,
  scheme,
  setScheme,
}: DisplayOptionsProps) {
  return (
    <div className="relative inline-block">
      <Button
        onClick={toggle}
        variant="outline"
        size="icon"
        title="Display options"
      >
        <Paintbrush2 className="h-4 w-4" />
      </Button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 p-3 bg-background border rounded-md shadow-lg z-50 space-y-3">
          {/* Improve column names */}
          <button
            onClick={() => setImproveNames(!improveNames)}
            className="flex items-center gap-2 w-full text-sm hover:bg-muted px-2 py-1 rounded"
          >
            <span
              className={`w-4 h-4 border rounded flex items-center justify-center ${
                improveNames
                  ? "border-brand bg-brand/10"
                  : "border-muted-foreground/40"
              }`}
            >
              {improveNames && <Check className="h-3 w-3 text-brand" />}
            </span>
            Improve column names
          </button>

          {/* Colour scheme */}
          <div className="space-y-1 text-sm">
            <div className="font-medium mb-1">Colour scheme</div>
            {(["default", "blue", "gray", "green"] as ColorScheme[]).map(
              (s) => (
                <button
                  key={s}
                  onClick={() => setScheme(s)}
                  className={`block w-full text-left px-2 py-1 rounded hover:bg-muted ${
                    scheme === s ? "font-semibold text-brand" : ""
                  }`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
